/**
 * Channel choice for the notifications this phone raises itself (the event
 * reminder a trainer sets from a bonus card). They never touch the server,
 * so the choice lives here, per device, and the sender asks before it fires
 * (2026-09-11). The live scan and trade completion banners were retired the
 * same day: a trainer who is already scanning or marking a trade needs no
 * notification about it.
 *
 *   "off"    → nothing
 *   "in-app" → toast while the app is open, no OS notification
 *   "push"   → the OS notification as well
 */
import { NOTIF_EVENTS, type DeviceNotifId, type NotifChannel } from "./notifChannels";

const KEY = "nx.notif.device.v1";

type Stored = Partial<Record<DeviceNotifId, NotifChannel>>;

function read(): Stored {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as Stored) : {};
  } catch {
    return {};
  }
}

function isChannel(v: unknown): v is NotifChannel {
  return v === "off" || v === "in-app" || v === "push";
}

/** The trainer's choice for a device event, or its default. */
export function deviceChannel(id: DeviceNotifId): NotifChannel {
  const stored = read()[id];
  if (isChannel(stored)) return stored;
  const event = NOTIF_EVENTS.find((e) => e.id === id);
  return event?.deviceDefault ?? "push";
}

export function setDeviceChannel(id: DeviceNotifId, channel: NotifChannel): void {
  try {
    const next = { ...read(), [id]: channel };
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode: the default applies */
  }
}

/** What a sender may do right now for a device event. */
export function deviceDelivery(id: DeviceNotifId): { toast: boolean; banner: boolean } {
  const c = deviceChannel(id);
  return { toast: c !== "off", banner: c === "push" };
}

/** Tests only. */
export function __resetDeviceNotifPrefsForTest(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
